"use client";

import { useEffect, useState } from "react";
import { useExperiment } from "@/lib/analytics/use-experiment";
import { fetchGuardrailConfig } from "@/lib/monetization/use-ad-guardrails";
import { CLIENT_RESOLVE_TIMEOUT_MS } from "@/lib/net/fetch-with-timeout";
import {
	ADSENSE_SLOT_EXPERIMENT_FLAG,
	type AdsenseArmDecision,
	type AdsenseConfig,
	DEFAULT_ADSENSE_CONFIG,
	resolveAdsenseArm,
} from "./experiment";

// Content is irrelevant here (the arm is a decision, not copy); `useExperiment`
// only needs the branch names so it can normalise the raw PostHog value and
// reject anything that is not a configured variant.
const VARIANTS = { control: null, adsense: null, first_party: null };

export type AdsenseArmResult = AdsenseArmDecision & {
	/**
	 * False until BOTH the experiment flag and the payload overrides have
	 * resolved, or the bounded wait below expires. While false the caller must
	 * not mount AdSense: an `<ins>` that mounts and is then withdrawn has already
	 * asked Google for an ad, and would count an impression in the wrong arm.
	 */
	ready: boolean;
};

const NOT_READY: AdsenseArmResult = {
	ready: false,
	allowed: false,
	label: "default",
	reason: "default",
};

/**
 * Which AdSense arm this visitor is in for `slot`, resolved client-side because
 * every page hosting an `AdSlot` is ISR/edge-cached and cannot see the visitor.
 *
 * Bounded (`CLIENT_RESOLVE_TIMEOUT_MS`, 1.5 s): PostHog can be blocked, slow, or
 * absent (no consent), and a hang must degrade to the DEFAULT arm (AdSense
 * allowed, unlabelled) rather than blank a revenue slot. The flag is the sole
 * switch: no hardcoded constant shadows it, and "absent" means today's behaviour.
 */
export function useAdsenseArm(slot: string, market: string): AdsenseArmResult {
	const { variantKey, isReady } = useExperiment({
		flagKey: ADSENSE_SLOT_EXPERIMENT_FLAG,
		variants: VARIANTS,
	});
	const [config, setConfig] = useState<AdsenseConfig | null>(null);
	const [waitExpired, setWaitExpired] = useState(false);

	useEffect(() => {
		let cancelled = false;
		fetchGuardrailConfig().then((c) => {
			if (!cancelled) setConfig(c.adsense);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		const timer = setTimeout(
			() => setWaitExpired(true),
			CLIENT_RESOLVE_TIMEOUT_MS,
		);
		return () => clearTimeout(timer);
	}, []);

	const settled = (isReady && config !== null) || waitExpired;
	if (!settled) return NOT_READY;

	return {
		ready: true,
		...resolveAdsenseArm({
			slot,
			market,
			// An unresolved flag at the deadline is the DEFAULT arm, not a bucket.
			variant: isReady ? variantKey : "control",
			config: config ?? DEFAULT_ADSENSE_CONFIG,
		}),
	};
}
